import { useEffect } from 'react';
import { Navigate, useParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { SUPPORTED_LANGS, DEFAULT_LANG, LANG_STORAGE_KEY } from './index.js';

const detectPreferredLang = () => {
  if (typeof window === 'undefined') return DEFAULT_LANG;

  const stored = window.localStorage.getItem(LANG_STORAGE_KEY);
  if (stored && SUPPORTED_LANGS.includes(stored)) return stored;

  const browserLang = (window.navigator.language || '').slice(0, 2).toLowerCase();
  return SUPPORTED_LANGS.includes(browserLang) ? browserLang : DEFAULT_LANG;
};

/**
 * RootRedirect
 * Handles "/" and any un-prefixed path (e.g. an old bookmark to "/terms")
 * by prepending a detected language and redirecting — "/" -> "/en",
 * "/terms" -> "/en/terms". First visit respects the browser's language,
 * same idea as the dark-mode auto-detect; once someone picks a language
 * via <LanguageSwitcher />, that choice is saved and wins from then on.
 */
export const RootRedirect = () => {
  const location = useLocation();
  const lang = detectPreferredLang();
  const suffix = location.pathname === '/' ? '' : location.pathname;
  return <Navigate to={`/${lang}${suffix}`} replace />;
};

/**
 * LangGate
 * Wraps every "/:lang/..." route. Validates the :lang param, redirects to
 * "/en" if it's not a supported language, and keeps i18next + <html lang>
 * in sync with the URL.
 */
export const LangGate = ({ children }) => {
  const { lang } = useParams();
  const { i18n } = useTranslation();

  useEffect(() => {
    if (!SUPPORTED_LANGS.includes(lang)) return;
    i18n.changeLanguage(lang);
    document.documentElement.lang = lang;
    window.localStorage.setItem(LANG_STORAGE_KEY, lang);
  }, [lang, i18n]);

  if (!SUPPORTED_LANGS.includes(lang)) {
    return <Navigate to={`/${DEFAULT_LANG}`} replace />;
  }

  return children;
};
